import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from '@/common/hooks/use-query-params';
import { createAgentChatThread } from '../services/chat';
import { clearCreationQuery, getCreationRecordQuery } from '../utils/creation-record';
import { getCreationInput } from '../utils/harness-creation';

export function useCreationStart({ organizationId, entry }) {
  const location = useLocation();
  const [query, setQuery] = useQueryParams();
  const currentLocation = useRef(location);
  const request = useRef();
  const automaticLocation = useRef();
  const [state, setState] = useState({});
  currentLocation.current = location;
  useEffect(() => {
    currentLocation.current = location;
    return () => {
      currentLocation.current = null;
    };
  }, [location]);

  const start = useCallback(
    async (input, { replace = false } = {}) => {
      if (request.current?.location === location && request.current.pending) return false;
      const attempt = { location, input, replace, pending: true };
      request.current = attempt;
      const isCurrent = () => currentLocation.current === location && request.current === attempt;
      setState({ location, submitting: true });
      try {
        const { idea, ...source } = getCreationInput(input);
        const record = await createAgentChatThread({
          organizationId,
          ...(idea === undefined ? { entry: source } : {}),
        });
        if (!isCurrent()) return false;
        setQuery(
          { ...clearCreationQuery(query), ...getCreationRecordQuery(record, idea) },
          { reset: true, replace },
        );
        setState({ location, submitting: false });
        return true;
      } catch (error) {
        if (isCurrent()) setState({ location, error, submitting: false });
        return false;
      } finally {
        // eslint-disable-next-line require-atomic-updates -- Each invocation owns its attempt object.
        attempt.pending = false;
      }
    },
    [location, organizationId, query, setQuery],
  );

  useEffect(() => {
    if (!entry || automaticLocation.current === location) return;
    automaticLocation.current = location;
    start(entry, { replace: true });
  }, [entry, location, start]);

  return {
    start,
    submitting: state.location === location && Boolean(state.submitting),
    error: state.location === location ? state.error : undefined,
    retry: () => {
      const previous = request.current;
      if (previous?.location === location)
        return start(previous.input, { replace: previous.replace });
      return undefined;
    },
  };
}
